import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../services/api';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // Tracks which specific toggle is loading
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  // Spec: Pending and Cancelled are strictly prohibited from having toggles
  const CONFIGURABLE_STATUSES = ['In Progress', 'Ready', 'Delivered'];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationAPI.getSettings();
      
      // Convert the array payload [{status: 'Ready', is_enabled: true}] into an O(1) lookup object
      const settingsMap = {};
      response.data.settings.forEach(s => {
        settingsMap[s.status] = s.is_enabled;
      });
      setSettings(settingsMap);
    } catch (err) {
      setError('Failed to load notification settings. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (status, currentValue) => {
    try {
      setUpdating(status);
      setError(null);
      
      const newValue = !currentValue;
      
      // Hit the PUT endpoint
      await notificationAPI.updateSettings(status, newValue);
      
      // Optimistically update the UI state on success
      setSettings(prev => ({ ...prev, [status]: newValue }));
      
      // Trigger success toast
      setToast(`${status} notifications turned ${newValue ? 'ON' : 'OFF'}`);
      setTimeout(() => setToast(null), 3000);
      
    } catch (err) {
      setError(`Failed to update setting for ${status}.`);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-12">
      
      {/* Header Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">WhatsApp Notifications</h2>
        <p className="text-gray-500 mt-1 text-sm font-medium">Configure automated messaging triggers for status transitions.</p>
      </div>

      {/* Alert Banners */}
      {error && (
        <div className="mb-4 bg-red-50 text-red-700 p-4 rounded-lg font-medium border border-red-200">
          {error}
        </div>
      )}

      {toast && (
        <div className="mb-4 bg-green-50 text-green-700 p-4 rounded-lg font-bold border border-green-200 flex items-center shadow-sm transition-opacity duration-300">
          <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
          {toast}
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          
          {CONFIGURABLE_STATUSES.map(status => {
            const isEnabled = settings[status] || false;
            const isUpdating = updating === status;

            return (
              <div key={status} className="p-5 sm:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="pr-4">
                  <h3 className="text-base font-bold text-gray-900">{status}</h3>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                    Send an automated WhatsApp message to the customer when their order is marked as {status}.
                  </p>
                </div>
                
                {/* Custom Tailwind Toggle Switch */}
                <button
                  onClick={() => handleToggle(status, isEnabled)}
                  disabled={isUpdating}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isEnabled ? 'bg-green-500' : 'bg-gray-300'
                  } ${isUpdating ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                  aria-pressed={isEnabled}
                >
                  <span className="sr-only">Toggle {status} notification</span>
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
                      isEnabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            );
          })}

        </div>

        {/* Spec Disclaimer Footer */}
        <div className="bg-gray-50 p-5 border-t border-gray-200 text-sm text-gray-600 flex items-start">
          <svg className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>
            <span className="font-bold text-gray-800">Note: </span>
            Cancelled and Pending orders never send automated notifications. This rule is enforced at the system level.
          </p>
        </div>
      </div>

    </div>
  );
};

export default NotificationSettings;